import React from 'react';
import {
  useParams, Link, useHistory
} from "react-router-dom";
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Container from '@material-ui/core/Container';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import { Close } from '@material-ui/icons';
import Snackbar from '@material-ui/core/Snackbar';
import { Divider } from '@material-ui/core';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import GooglePlacesAutocomplete, {
  geocodeByAddress,
} from "react-google-places-autocomplete";

import Config from './config';
import { checkMobileNumber, formatMobileNumber, ValidateEmail } from './utils';

const googlePlaceAddressForm: Record<string, any> = {
  street_number: "short_name",
  route: "long_name",
  locality: "long_name",
  administrative_area_level_1: "short_name",
  postal_code: "short_name",
};

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    paddingBottom: 50,
    paddingTop: 10,
    height: '100%',
    backgroundColor: '#EDF2F2'
  },
  language: {
    position: 'absolute',
    right: 3,
    top: 3,
  },
  header: {
    textAlign: 'center',
    padding: theme.spacing(4),
    paddingBottom: theme.spacing(2),
  },
  form: {
    '& > *': {
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
    textAlign: 'center',
    minWidth: 280,
    maxWidth: 400,
    margin: '0 auto',
  },
  formControl: {
    width: 320,
    backgroundColor: '#FFF',
  },
  containerGrid: {
    width: 320,
    margin: '0 auto',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  formControlGird1: {
    padding: `0px !important`,
    paddingRight: `4px !important`,
  },
  formControlGird2: {
    padding: `0px !important`,
    paddingLeft: `4px !important`,
  },
  formControlHalf: {
    width: 156,
  },
  addressFormControl: {
    width: 320,
    margin: '0 auto',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
    '& > div > div': {
      minHeight: 56,
    },
    '& > *': {
      fontFamily: `${theme.typography.fontFamily} !important`,
    },
  },
  button: {
    width: 320,
    height: 56,
    margin: '0 auto',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
    // color: 'white',
    fontSize: 18,
  },
  link: {
    fontWeight: 600,
    color: 'black',
    fontSize: 16,
  }
}));

function RegisterStore({ analytics }: { analytics: any }) {
  // const { t } = useTranslation();
  const classes = useStyles();
  let history = useHistory();
  const { registrationId }: { registrationId: string } = useParams();

  const [fullName, setFullName] = React.useState<string>('');
  const [mobile, setMobile] = React.useState<string>('');
  const [password, setPassword] = React.useState<string>('');
  const [email, setEmail] = React.useState<string>('');
  const [storeName, setStoreName] = React.useState<string>('');
  const [abn, setAbn] = React.useState<string>('');
  const [address, setAddress] = React.useState<Record<string, any>>();
  const [confirmDetail, setConfirmDetail] = React.useState<boolean>(false);

  const [googlePlace, setGooglePlace] = React.useState<any>();
  const [snackbarOpen, setSnackbarOpen] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const [regisiterCompleted, setRegisiterCompleted] = React.useState<boolean>(false);

  const handleSelect = async (value: any) => {
    setGooglePlace(value);
    const results = await geocodeByAddress(value.label);
    fillInAddress(results[0]);
  };

  const fillInAddress = (place: any) => {
    const googlePlaceAddress: Record<string, any> = {
      street_number: '',
      route: '',
      locality: "",
      administrative_area_level_1: "",
      postal_code: "",
    };
    for (const component of place.address_components) {
      const addressType: string = component.types[0];

      if (googlePlaceAddressForm[addressType]) {
        const value = component[googlePlaceAddressForm[addressType]];
        googlePlaceAddress[addressType] = value;
      }
    }
    setAddress(googlePlaceAddress);
  }

  const handleSubmit = () => {
    setErrorMessage('');
    if (!fullName.trim() || !mobile.trim() || !email.trim() || !password.trim() || !storeName.trim() || !abn.trim() || !address) {
      setErrorMessage('Please enter all required information');
      setSnackbarOpen(true);
      return;
    }

    if (password.trim().length < 6) {
      setErrorMessage('Password must be at least 6 characters');
      setSnackbarOpen(true);
      return;
    }

    if (!checkMobileNumber(mobile.trim())) {
      setErrorMessage('Please enter a valid mobile number');
      setSnackbarOpen(true);
      return;
    }

    if (!ValidateEmail(email.trim())) {
      setErrorMessage('Please enter a valid email address');
      setSnackbarOpen(true);
      return;
    }

    if (abn.trim().length !== 11) {
      setErrorMessage('Please enter a valid ABN');
      setSnackbarOpen(true);
      return;
    }

    if (!confirmDetail) {
      setErrorMessage('Please Confirm these details are correct');
      setSnackbarOpen(true);
      return;
    }

    createStore();
  }

  const handleClose = (event: React.SyntheticEvent | React.MouseEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const createStore = async () => {
    try {
      // register user
      const regisiterUserResponse = await fetch(`${Config.apiUrl}account/register`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            mobile_number: formatMobileNumber(mobile.trim()),
            password: password.trim(),
            full_name: fullName.trim(),
          })
        }
      );
      if (!regisiterUserResponse.ok) {
        let message = `An error has occured: ${regisiterUserResponse.status}`;
        if (regisiterUserResponse.status === 422) {
          message = 'Mobile number already exists, please change number.'
        }
        throw new Error(message);
      }

      // login to get token
      const loginResponse = await fetch(`${Config.apiUrl}account/login/username`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: formatMobileNumber(mobile.trim()),
            password: password.trim(),
            principle: 'MOBILE',
          })
        }
      );
      if (!loginResponse.ok) {
        const message = `An error has occured: ${loginResponse.status}`;
        throw new Error(message);
      }
      const userProfile = await loginResponse.json();
      const { accessToken } = userProfile;
      localStorage.setItem('accessToken', accessToken);

      // update user profile email 
      const updateUserResponse = await fetch(`${Config.apiUrl}account/update-user`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: accessToken,
          },
          body: JSON.stringify({
            email: email.trim(),
          })
        }
      );
      if (!updateUserResponse.ok) {
        const message = `An error has occured: ${updateUserResponse.status}`;
        throw new Error(message);
      }

      // register store
      const storePayload: any = {
        storeName: storeName.trim(), abn: abn.trim(),
        industry: Config.industry,
        address: {
          address1: `${address?.street_number} ${address?.route}`,
          address2: '',
          city: address?.locality,
          lat: Config.lat,
          lng: Config.lng,
          postCode: address?.postal_code,
          state: address?.administrative_area_level_1,
        },
      };
      if (registrationId) storePayload.registrationId = registrationId;
      const storeResponse = await fetch(`${Config.apiUrl}store/v3/store/register`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: accessToken,
          },
          body: JSON.stringify(storePayload),
        }
      );
      if (!storeResponse.ok) {
        const message = `An error has occured: ${storeResponse.status}`;
        throw new Error(message);
      }
      const storeData = await storeResponse.json();
      const { id } = storeData;

      console.log(id);
      localStorage.setItem('storeId', id);

      //update store profile 
      const storeUpdateResponse = await fetch(`${Config.apiUrl}store/profile/${id}`,
        {
          method: 'PUT',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: accessToken,
          },
          body: JSON.stringify({
            logoImgUrl: Config.storeLogoUrl,
            mapContent: Config.storeMapUrl,
            bannerUrl: [Config.storeBannerUrl],
          })
        }
      );
      if (!storeUpdateResponse.ok) {
        const message = `An error has occured: ${storeUpdateResponse.status}`;
        throw new Error(message);
      }

      console.log('store regisiter success');
      if (registrationId) {
        analytics().logEvent('store_regisiter_success_with_registrationId');
      } else {
        analytics().logEvent('store_regisiter_success');
      }
      setRegisiterCompleted(true);

    } catch (error) {
      console.error(error.message)
      setErrorMessage(error.message);
      setSnackbarOpen(true);
    }
  };

  return (
    <div className={classes.root}>
      {regisiterCompleted ?
        <Container className={classes.header}>
          <Typography variant="h1" component="h2" style={{ fontSize: 24, marginBottom: 100, marginTop: 100, fontWeight: 400 }} >Registration Confirmed!</Typography>
          <Typography variant="h3" component="h1" style={{ color: 'black', marginTop: 20, marginBottom: 30, fontSize: 15, fontWeight: 700 }} >
            Thank you for registering for GuestBook.<br />Your store is now activated.
          </Typography>
          <Divider />
          {registrationId ?
            <Typography variant="body2" component="p" style={{ color: 'black', marginTop: 30 }} >
              You can immediately start using your QR code in your venue for customers to scan and check-in.<br /><br />To access these records log into our management centre’s web portal at<br /><br /><Link to="/admin/regisiterrecords" className={classes.link}>guestbook.linkpos.com.au/#/admin</Link><br />using your mobile and password.
          </Typography>
            : null}
          {!registrationId ?
            <React.Fragment>
              <Typography variant="body2" component="p" style={{ color: 'black', marginTop: 30 }} >
                You can download your QR code for customers to scan and check-in.
          </Typography>
              <Button
                onClick={() => history.push('/admin/qrcode')}
                variant="contained"
                color="primary"
                disableElevation
                size="large"
                className={classes.button}
              >
                Download QR Code
        </Button>
              <Typography variant="body2" component="p" style={{ color: 'black', marginTop: 30 }} >
                To access these records log into our management centre’s web portal at<br /><br /><Link to="/admin/regisiterrecords" className={classes.link}>guestbook.linkpos.com.au/#/admin</Link><br />using your mobile and password.
          </Typography>
            </React.Fragment>
            : null}
        </Container>
        :
        <React.Fragment>
          <Container className={classes.header}>
            <Typography variant="h1" component="h2" style={{ fontSize: 24, marginBottom: 20, fontWeight: 400 }} >GuestBook Registration For Businesses</Typography>
            <Typography variant="body2" component="p" style={{ marginBottom: 20 }} >GuestBook is a free QR code system that allows you to collect data from every customer, quickly and securely for contact tracing and COVID-19 compliance. Learn more about GuestBook here.</Typography>

            <Divider />
            <Typography variant="h3" component="h1" style={{ color: 'black', marginTop: 20, fontSize: 15, fontWeight: 700 }} >
              Register Your Business For Free
            </Typography>
            <Typography variant="body2" component="p" style={{ color: 'black', marginTop: 20 }} >
              Please enter your details below and your store will be immediately activated.
            </Typography>
          </Container>
          <div className={classes.form} >
            <TextField
            color="secondary"
              fullWidth
              className={classes.formControl}
              autoComplete="given-name"
              required id="fullName"
              label="Owner Name"
              variant="outlined"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
            />
            <TextField
            color="secondary"
              type="number"
              fullWidth
              className={classes.formControl}
              autoComplete="tel"
              required id="mobile"
              label="Mobile Number"
              variant="outlined"
              value={mobile}
              onChange={e => setMobile(e.target.value)}
            />
            <TextField
            color="secondary"
              type="email"
              fullWidth
              className={classes.formControl}
              autoComplete="email"
              required id="email"
              label="Email"
              variant="outlined"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            <TextField
            color="secondary"
              fullWidth
              className={classes.formControl}
              type="password"
              autoComplete="new-password"
              required id="password"
              label="Password"
              variant="outlined"
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
            <TextField
            color="secondary"
              fullWidth
              className={classes.formControl}
              required id="storeName"
              label="Business Name"
              variant="outlined"
              value={storeName}
              onChange={e => setStoreName(e.target.value)}
            />
            <TextField
            color="secondary"
              fullWidth
              type="number"
              className={classes.formControl}
              required id="abn"
              label="ABN"
              variant="outlined"
              value={abn}
              onChange={e => setAbn(e.target.value)}
            />
            <div className={classes.addressFormControl}>
              <GooglePlacesAutocomplete
                apiKey="AIzaSyAyaXfxzi-i5N6m9DYeK6-SHCmJTJqTZuA"
                autocompletionRequest={{
                  componentRestrictions: {
                    country: ['au'],
                  }
                }}
                selectProps={{
                  value: googlePlace,
                  onChange: handleSelect,
                  placeholder: 'Business Address*',
                }} />
            </div>
            <FormControlLabel control={<Switch
              checked={confirmDetail}
              color="primary"
              onChange={() => setConfirmDetail(!confirmDetail)}
              name="saveCustomerInfo"
              inputProps={{ 'aria-label': 'secondary checkbox' }}
            />} label={
              <Typography variant="body2" component="p" style={{ color: 'black' }} >
                I confirm these details are correct.
            </Typography>
            } />
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disableElevation
              size="large"
              className={classes.button}
            >
              Register Now
        </Button>

          </div>
          <Snackbar
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            open={snackbarOpen}
            autoHideDuration={2000}
            onClose={handleClose}
            message={errorMessage}
            action={
              <React.Fragment>
                <IconButton size="small" aria-label="close" color="inherit" onClick={() => setSnackbarOpen(false)}>
                  <Close fontSize="small" />
                </IconButton>
              </React.Fragment>
            }
          /></React.Fragment>
      }
    </div >
  );
}

export default RegisterStore;
